import React, { useMemo, useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";

import cls from "./ChatList.module.scss";

import avatarPng from "@/assets/UI/avatar.png";
import api from "@/utils/axios";
import { useAuth } from "@/app/auth-context-state";
import { connectSocket, getSocket, releaseSocket } from "@/utils/socket";

function formatRelativeTime(dateInput) {
  if (!dateInput) return "";
  const created =
    typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (Number.isNaN(created.getTime())) return "";

  const now = new Date();
  const diffMs = now - created;
  if (diffMs < 0) return "";

  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return "just now";

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return `${diffMin} min${diffMin > 1 ? "s" : ""} ago`;
  }

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) {
    return `${diffHr} hour${diffHr > 1 ? "s" : ""} ago`;
  }

  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) {
    return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
  }

  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12) {
    return `${diffMonth} month${diffMonth > 1 ? "s" : ""} ago`;
  }

  const diffYear = Math.floor(diffMonth / 12);
  return `${diffYear} year${diffYear > 1 ? "s" : ""} ago`;
}

const ChatList = () => {
  const location = useLocation();
  const isChat = location.pathname === "/admin/chat";
  const { user } = useAuth();
  const currentUserId = user?._id || user?.id || user?.userId || null;
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query] = useState("");
  const [onlineUserIds, setOnlineUserIds] = useState(() => new Set());

  const accessToken = (() => {
    try {
      return localStorage.getItem("cm_at");
    } catch {
      return null;
    }
  })();

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/v1/admin/chat/conversations");
      setConversations(res.data?.conversations || []);
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          "Unable to load chats right now. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (!accessToken) return;
    const socket = getSocket() || connectSocket(accessToken);

    const handlePresenceList = (payload) => {
      const ids = Array.isArray(payload) ? payload : [];
      setOnlineUserIds(new Set(ids.map((id) => String(id))));
    };

    const handleOnline = (payload) => {
      const userId = payload?.userId;
      if (!userId) return;
      setOnlineUserIds((prev) => {
        const next = new Set(prev);
        next.add(String(userId));
        return next;
      });
    };

    const handleOffline = (payload) => {
      const userId = payload?.userId;
      if (!userId) return;
      setOnlineUserIds((prev) => {
        const next = new Set(prev);
        next.delete(String(userId));
        return next;
      });
    };

    socket.on("presence:list", handlePresenceList);
    socket.on("presence:online", handleOnline);
    socket.on("presence:offline", handleOffline);

    return () => {
      const s = getSocket();
      if (s) {
        s.off("presence:list", handlePresenceList);
        s.off("presence:online", handleOnline);
        s.off("presence:offline", handleOffline);
      }
      releaseSocket();
    };
  }, [accessToken]);

  const chats = useMemo(() => {
    const getParticipantId = (participant) =>
      participant?.userId || participant?._id || participant?.id || null;

    return conversations
      .map((conversation) => {
        const participants = conversation.participants || [];
        if (
          currentUserId &&
          !participants.some(
            (participant) =>
              String(getParticipantId(participant)) === String(currentUserId),
          )
        ) {
          return null;
        }

        const otherParticipant = currentUserId
          ? participants.find(
              (participant) =>
                String(getParticipantId(participant)) !== String(currentUserId),
            )
          : null;
        const memberTargets = participants.filter(
          (participant) => participant?.memberId,
        );
        const target =
          otherParticipant || memberTargets[0] || participants[0] || null;
        const participantUserId = target?.userId || null;
        const lastMessage = conversation.lastMessage;
        const lastMessageText = lastMessage?.text?.trim();
        const preview =
          lastMessageText ||
          (lastMessage?.attachments?.length
            ? "Sent an attachment"
            : "No messages yet");
        const unread =
          lastMessage &&
          String(lastMessage.senderId) !== String(currentUserId) &&
          lastMessage.status !== "read"
            ? 1
            : 0;
        const timeLabel = formatRelativeTime(
          lastMessage?.createdAt || conversation.lastMessageAt,
        );
        return {
          id: conversation.id || conversation._id,
          memberId: target?.memberId || getParticipantId(target) || null,
          name: target?.username || "Conversation",
          avatar: target?.avatarUrl || avatarPng,
          participantUserId,
          isOnline: participantUserId
            ? onlineUserIds.has(String(participantUserId))
            : false,
          lastMessage: preview,
          time: timeLabel,
          unread,
        };
      })
      .filter(Boolean);
  }, [conversations, currentUserId, onlineUserIds]);

  const filteredChats = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return chats;
    return chats.filter((chat) => {
      const nameMatch = chat.name.toLowerCase().includes(trimmed);
      const msgMatch = chat.lastMessage?.toLowerCase().includes(trimmed);
      return nameMatch || msgMatch;
    });
  }, [chats, query]);

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        <nav className={cls.tabs}>
          <Link to="/admin" className={cls.tabButton}>
            Dashboard
          </Link>
          <Link to="/admin/members" className={cls.tabButton}>
            Members
          </Link>
          <Link
            to="/admin/chat"
            className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
          >
            Chat
          </Link>
        </nav>

        <div className={cls.chatLayout}>
          <section className={cls.chatListPanel}>
            <header className={cls.panelHeader}>
              <div>
                <h1 className={cls.panelTitle}>Chats</h1>
                <p className={cls.panelSubtitle}>
                  Stay in touch with members from the admin panel.
                </p>
              </div>
            </header>

            <div className={cls.chatList}>
              {loading && <p>Loading chats...</p>}
              {!loading && error && <p>{error}</p>}
              {!loading && !error && filteredChats.length === 0 && (
                <p>No chats yet. Start a conversation to see it here.</p>
              )}
              {!loading &&
                !error &&
                filteredChats.map((chat) => (
                  <Link
                    key={chat.id}
                    to={`/admin/chat/${chat.memberId || chat.id}`}
                    className={cls.chatRow}
                  >
                    <div className={cls.avatarWrap}>
                      <img src={chat.avatar} alt={chat.name} />
                      <span
                        className={`${cls.onlineDot} ${
                          chat.isOnline
                            ? cls.onlineDotOnline
                            : cls.onlineDotOffline
                        }`}
                      />
                    </div>
                    <div className={cls.chatMeta}>
                      <div className={cls.chatTopRow}>
                        <span className={cls.chatName}>{chat.name}</span>
                        <span className={cls.chatTime}>{chat.time}</span>
                      </div>
                      <div className={cls.chatBottomRow}>
                        <span
                          className={`${cls.presenceLabel} ${
                            chat.isOnline
                              ? cls.presenceOnline
                              : cls.presenceOffline
                          }`}
                        >
                          {chat.isOnline ? "Online" : "Offline"}
                        </span>
                        <span className={cls.chatPreview}>
                          {chat.lastMessage}
                        </span>
                        {chat.unread > 0 && (
                          <span className={cls.unreadBadge}>{chat.unread}</span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </section>

          <section className={cls.chatEmptyPanel}>
            <div className={cls.emptyCard}>
              <h2>Open a conversation</h2>
              <p>
                Select a chat to see messages. Your conversations stay synced
                across every admin view.
              </p>
              <div className={cls.emptyActions}>
                <Link to="/admin/members" className={cls.primaryButton}>
                  View members
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ChatList;
